import { Link, useNavigate } from 'react-router-dom';
import { useStore } from '../../shared/stores/context';
import { observer } from 'mobx-react-lite';
import styles from './Sidebar.module.css';
import { useEffect, useState } from 'react';
import {
  Home,
  ChevronDown,
  Briefcase,
  Newspaper,
  Calendar,
  ShoppingBag,
  Users,
  UserPlus,
  FileText,
  Star,
  Handshake,
  Building2,
  LogOut,
  Coins,
  FolderOpen,
  Tag,
  Factory,
  ShoppingCart,
  Ticket,
  TrendingUp,
  FileCheck,
  MessageSquare,
  BarChart3,
  Megaphone,
  Shield,
  Layers,
  Package,
} from 'lucide-react';

const MENU_CONFIG = [
  {
    section: 'Основное',
    items: [
      { type: 'link', to: '/', icon: Home, label: 'Главная' },
    ],
  },
  {
    section: 'Контент',
    items: [
      {
        type: 'dropdown',
        id: 'portfolio',
        icon: Briefcase,
        label: 'Портфолио',
        children: [
          { to: '/portfolio', icon: FolderOpen, label: 'Работы' },
          { to: '/portfolio/categories', icon: Tag, label: 'Категории' },
          { to: '/portfolio/industries', icon: Factory, label: 'Индустрии' },
        ],
      },
      {
        type: 'dropdown',
        id: 'posts',
        icon: Newspaper,
        label: 'Новости',
        children: [
          { to: '/posts', icon: FileText, label: 'Статьи' },
          { to: '/posts/categories', icon: Tag, label: 'Категории' },
          { to: '/posts/industries', icon: Factory, label: 'Индустрии' },
        ],
      },
      {
        type: 'dropdown',
        id: 'events',
        icon: Calendar,
        label: 'Мероприятия',
        children: [
          { to: '/events', icon: Calendar, label: 'События' },
          { to: '/events/categories', icon: Tag, label: 'Категории' },
          { to: '/events/formats', icon: Layers, label: 'Форматы' },
        ],
      },
    ],
  },
  {
    section: 'Бизнес',
    items: [
      {
        type: 'dropdown',
        id: 'shop',
        icon: ShoppingBag,
        label: 'Магазин',
        children: [
          { to: '/shop/products', icon: Package, label: 'Товары' },
          { to: '/shop/products/categories', icon: Tag, label: 'Категории' },
          { to: '/shop/orders', icon: ShoppingCart, label: 'Заказы' },
          { to: '/shop/promocodes', icon: Ticket, label: 'Промокоды' },
        ],
      },
      {
        type: 'dropdown',
        id: 'hr',
        icon: Users,
        label: 'HR',
        children: [
          { to: '/HR/vacancies', icon: Briefcase, label: 'Вакансии' },
          { to: '/HR/categories', icon: Tag, label: 'Категории' },
        ],
      },
      {
        type: 'dropdown',
        id: 'referral',
        icon: UserPlus,
        label: 'Реферальная программа',
        children: [
          { to: '/referral-program/members', icon: Users, label: 'Участники' },
          { to: '/referral-program/withdrawal-requests', icon: Coins, label: 'Заявки на выплату' },
        ],
      },
    ],
  },
  {
    section: 'Прочее',
    items: [
      {
        type: 'dropdown',
        id: 'other',
        icon: FileText,
        label: 'Дополнительно',
        children: [
          { to: '/invest-projects', icon: TrendingUp, label: 'Инвест проекты' },
          { to: '/documents', icon: FileCheck, label: 'Договоры' },
          { to: '/others/applications-form', icon: MessageSquare, label: 'Заявки с форм' },
          { to: '/stats/visits-days-count', icon: BarChart3, label: 'Статистика' },
          { to: '/others/news', icon: Megaphone, label: 'СМИ о нас' },
          { to: '/others/documents', icon: Shield, label: 'Комплаенс' },
          { to: '/others/partners', icon: Handshake, label: 'Партнёры' },
          { to: '/reviews', icon: Star, label: 'Отзывы' },
          { to: '/services', icon: Building2, label: 'Услуги' },
          { to: '/outstaff', icon: Users, label: 'Outstaff' },
          { to: '/team-structures', icon: Layers, label: 'Команда' },
          { to: '/users', icon: Users, label: 'Пользователи' },
        ],
      },
    ],
  },
];

export const Sidebar = observer(() => {
  const [avatar, setAvatar] = useState('');
  const { userStore } = useStore();
  const navigate = useNavigate();
  const [openSubmenu, setOpenSubmenu] = useState(null);

  const toggleSubmenu = (menuId) => {
    setOpenSubmenu(openSubmenu === menuId ? null : menuId);
  };

  useEffect(() => {
    if (userStore.user.avatarPath) {
      setAvatar(userStore.user.avatarPath);
    }
  }, [userStore.user.avatarPath]);

  const renderMenuItem = (item) => {
    if (item.type === 'link') {
      const Icon = item.icon;
      return (
        <li key={item.to}>
          <Link to={item.to} className={styles.navLink}>
            <span className={styles.navIcon}><Icon size={20} /></span>
            <span className={styles.navText}>{item.label}</span>
          </Link>
        </li>
      );
    }

    if (item.type === 'dropdown') {
      const Icon = item.icon;
      const isOpen = openSubmenu === item.id;
      return (
        <li key={item.id}>
          <div
            className={styles.dropdownToggle}
            onClick={() => toggleSubmenu(item.id)}
            aria-expanded={isOpen}
          >
            <span className={styles.navIcon}><Icon size={20} /></span>
            <span className={styles.navText}>{item.label}</span>
            <span className={`${styles.dropdownIcon} ${isOpen ? styles.dropdownIconRotated : ''}`}>
              <ChevronDown size={18} />
            </span>
          </div>
          <ul className={`${styles.submenu} ${isOpen ? styles.submenuOpen : styles.submenuClosed}`}>
            {item.children.map((child) => (
              <li key={child.to}>
                <Link to={child.to} className={styles.submenuLink}>
                  {child.label}
                </Link>
              </li>
            ))}
          </ul>
        </li>
      );
    }
    return null;
  };

  return (
    <div className="col-md-12 col-lg-3 sidebar-desktop">
      <aside className="appSidebar flex-shrink-0 sidebar py-4">
        <div className={styles.sidebarHeader}>
          <Link to={'/'} className={styles.logo}>
            <svg width="130" height="50" viewBox="0 0 130 50" fill="none" xmlns="http://www.w3.org/2000/svg">
              <g clipPath="url(#clip0_1549_3519)">
                <path d="M49.3038 4.42993V28.9036C49.3038 30.965 50.9841 32.5878 53.0181 32.5878H60.6677C62.746 32.5878 64.382 34.2545 64.382 36.272C64.382 38.3334 62.7018 39.9562 60.6677 39.9562H45.2358C43.1575 39.9562 41.5215 38.2896 41.5215 36.272V4.42993C41.5215 2.36853 43.2018 0.745719 45.2358 0.745719H45.5895C47.6235 0.70186 49.3038 2.36853 49.3038 4.42993Z" fill="#061B2E"/>
                <path d="M94.0075 4.42993C94.0075 6.49133 92.3272 8.11414 90.2932 8.11414H81.9361C79.8578 8.11414 78.2218 9.78081 78.2218 11.7984V13.7282C78.2218 15.7896 79.902 17.4124 81.9361 17.4124H89.851C91.9292 17.4124 93.5653 19.0791 93.5653 21.0966C93.5653 23.158 91.885 24.7808 89.851 24.7808H81.9361C79.8578 24.7808 78.2218 26.4475 78.2218 28.465V36.2282C78.2218 38.2896 76.5415 39.9124 74.5075 39.9124H74.1537C72.0755 39.9124 70.4395 38.2457 70.4395 36.2282V4.42993C70.4395 2.36853 72.1197 0.745719 74.1537 0.745719H90.3374C92.3272 0.70186 94.0075 2.36853 94.0075 4.42993Z" fill="#061B2E"/>
                <path d="M112.269 9.12282C112.092 9.60528 112.092 10.1316 112.269 10.614L116.248 22.3684C116.779 23.8597 115.629 25.4386 114.037 25.4386H106.476H106.432H99.8436C98.8708 25.4386 97.9423 26.0526 97.6327 26.9737L94.228 36.6667C93.6974 38.1579 94.8028 39.7369 96.4389 39.7369H99.8878C100.905 39.7369 101.789 39.079 102.099 38.1579L103.425 34.2544C103.735 33.2895 104.619 32.6755 105.636 32.6755H118.061C119.078 32.6755 119.963 33.3333 120.272 34.2544L121.599 38.1579C121.908 39.1228 122.793 39.7369 123.81 39.7369H127.303C128.895 39.7369 130.044 38.1579 129.514 36.6667L118.592 5.83335C117.929 3.94738 115.32 3.72808 114.347 5.48247L112.49 8.77194C112.401 8.85966 112.313 8.99124 112.269 9.12282Z" fill="#061B2E"/>
                <path d="M18.6607 9.07912C18.4838 9.56158 18.4838 10.0879 18.6607 10.5703L22.6403 22.3247C23.1709 23.816 22.0212 25.3949 20.4294 25.3949H12.8239H12.7797H6.19127C5.21848 25.3949 4.28991 26.0089 3.98039 26.93L0.575624 36.623C0.0450119 38.1142 1.15045 39.6932 2.78651 39.6932H6.23549C7.2525 39.6932 8.13685 39.0353 8.44637 38.1142L9.7729 34.2107C10.0824 33.2458 10.9668 32.6318 11.9838 32.6318H24.409C25.426 32.6318 26.3103 33.2896 26.6198 34.2107L27.9464 38.1142C28.2559 39.0791 29.1403 39.6932 30.1573 39.6932H33.6505C35.2423 39.6932 36.392 38.1142 35.8613 36.623L24.9396 5.78965C24.2763 3.90368 21.6675 3.68438 20.6947 5.43877L18.8375 8.72824C18.7491 8.81596 18.7049 8.94754 18.6607 9.07912Z" fill="#061B2E"/>
                <path d="M117.974 43.158C117.93 43.2457 117.93 43.3773 117.974 43.465L118.814 46.0089C118.902 46.3159 118.681 46.6668 118.372 46.6668H116.78H115.409C115.188 46.6668 115.011 46.7984 114.967 47.0177L114.26 49.1229C114.127 49.4299 114.392 49.7808 114.702 49.7808H115.409C115.63 49.7808 115.807 49.6492 115.851 49.4299L116.117 48.5966C116.161 48.3773 116.382 48.2457 116.559 48.2457H119.124C119.345 48.2457 119.522 48.3773 119.566 48.5966L119.831 49.4299C119.875 49.6492 120.096 49.7808 120.273 49.7808H120.981C121.334 49.7808 121.556 49.4299 121.423 49.1229L119.168 42.4563C119.035 42.0615 118.505 42.0177 118.283 42.3685L117.885 43.0703C118.018 43.1141 118.018 43.1141 117.974 43.158Z" fill="#061B2E"/>
                <path d="M103.779 42.3245H107.494C107.98 42.3245 108.422 42.7192 108.422 43.2455C108.422 43.728 108.024 44.1666 107.494 44.1666H106.919C106.742 44.1666 106.565 44.342 106.565 44.5174V48.8157C106.565 49.2981 106.167 49.7367 105.637 49.7367H105.592C105.106 49.7367 104.664 49.342 104.664 48.8157V44.5174C104.664 44.342 104.487 44.1666 104.31 44.1666H103.735C103.249 44.1666 102.807 43.7718 102.807 43.2455C102.851 42.7631 103.249 42.3245 103.779 42.3245Z" fill="#061B2E"/>
                <path d="M111.03 47.3683V47.4122C111.03 47.6753 111.251 47.9385 111.561 47.9385H113.418C113.683 47.9385 113.949 48.1578 113.949 48.4648V49.2104C113.949 49.4736 113.728 49.7367 113.418 49.7367H109.66C109.394 49.7367 109.129 49.5174 109.129 49.2104V42.8508C109.129 42.5876 109.35 42.3245 109.66 42.3245H113.374C113.639 42.3245 113.904 42.5438 113.904 42.8508V43.5964C113.904 43.8596 113.683 44.1227 113.374 44.1227H111.561C111.296 44.1227 111.03 44.342 111.03 44.649C111.03 44.9122 111.251 45.1753 111.561 45.1753H113.108C113.374 45.1753 113.639 45.3946 113.639 45.7017V46.4034C113.639 46.6666 113.418 46.9297 113.108 46.9297H111.561C111.251 46.842 111.03 47.1052 111.03 47.3683Z" fill="#061B2E"/>
                <path d="M128.188 42.3245H128.807C129.249 42.3245 129.603 42.6753 129.603 43.1139V48.9034C129.603 49.342 129.249 49.6929 128.807 49.6929H128.542C128.1 49.6929 127.746 49.342 127.746 48.9034V48.1139C127.746 47.456 126.861 47.1929 126.508 47.7631L125.933 48.6841H125.756L125.181 47.7631C124.827 47.1929 123.943 47.456 123.943 48.1139V48.9034C123.943 49.342 123.589 49.6929 123.147 49.6929H122.882C122.44 49.6929 122.086 49.342 122.086 48.9034V43.1578C122.086 42.7192 122.44 42.3683 122.882 42.3683H123.501C123.766 42.3683 124.032 42.4999 124.208 42.7631L125.181 44.3859C125.491 44.9122 126.242 44.9122 126.552 44.3859L127.525 42.7631C127.613 42.4999 127.878 42.3245 128.188 42.3245Z" fill="#061B2E"/>
              </g>
              <defs>
                <clipPath id="clip0_1549_3519">
                  <rect width="130" height="50" fill="white"/>
                </clipPath>
              </defs>
            </svg>
          </Link>
        </div>

        <nav>
          {MENU_CONFIG.map((section) => (
            <div key={section.section} className={styles.navSection}>
              <h2 className={styles.navTitle}>{section.section}</h2>
              <ul className={styles.navList}>
                {section.items.map(renderMenuItem)}
              </ul>
            </div>
          ))}
        </nav>

        <div className={styles.profile} onClick={() => navigate(`/users/${userStore.user.id}`)}>
          {avatar && <img src={avatar} alt="Avatar" className={styles.profileAvatar} />}
          <div className={styles.profileInfo}>
            <p className={styles.profileName}>
              {userStore.user.name} {userStore.user.surname}
            </p>
            <p className={styles.profileRole}>Администратор</p>
          </div>
          <button
            onClick={(e) => {
              e.stopPropagation();
              userStore.logout();
            }}
            className={styles.logoutButton}
          >
            <LogOut size={20} />
          </button>
        </div>
      </aside>
    </div>
  );
});
